import { useEffect, useRef } from "react";
import { BookOpen, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { cn } from "@/lib/utils/cn";
import type { useChatSkillMentions } from "@/hooks/ai-chat/useChatSkillMentions";

export function ChatSkillSuggestions({
  mentions,
}: {
  mentions: ReturnType<typeof useChatSkillMentions>;
}) {
  const selected = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    selected.current?.scrollIntoView({ block: "nearest" });
  }, [mentions.selectedIndex]);

  if (!mentions.open) return null;
  return (
    <div
      className="bg-popover text-popover-foreground border-border absolute inset-x-0 bottom-full z-40 mb-2 overflow-hidden rounded-2xl border p-2 shadow-xl"
      onMouseDown={(event) => {
        event.preventDefault();
      }}
    >
      <div className="text-muted-foreground flex items-center justify-between px-3 py-2 text-xs">
        <span className="font-medium">Skills</span>
        <span>Type to search</span>
      </div>
      <div
        id={mentions.id}
        role="listbox"
        aria-label="Skills"
        aria-busy={mentions.pending}
        className="max-h-64 overflow-y-auto"
      >
        {mentions.pending ? (
          <p
            role="status"
            className="text-muted-foreground flex items-center gap-2 px-3 py-4 text-sm"
          >
            <LoaderCircle className="size-4 animate-spin" /> Loading skills…
          </p>
        ) : mentions.error ? (
          <div role="alert" className="px-3 py-3 text-sm">
            <p>Skills could not be loaded.</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={mentions.retry}
            >
              Try again
            </Button>
          </div>
        ) : !mentions.items.length ? (
          <p className="text-muted-foreground px-3 py-4 text-sm">
            No skills found.
          </p>
        ) : (
          mentions.items.map((skill, index) => (
            <button
              key={skill.id}
              ref={index === mentions.selectedIndex ? selected : undefined}
              id={`${mentions.id}-${index}`}
              type="button"
              role="option"
              aria-selected={index === mentions.selectedIndex}
              tabIndex={-1}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left",
                index === mentions.selectedIndex &&
                  "bg-accent text-accent-foreground",
              )}
              onMouseEnter={() => {
                mentions.setActiveIndex(index);
              }}
              onClick={() => {
                mentions.select(skill);
              }}
            >
              <span className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-lg">
                <BookOpen className="size-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {skill.name}
                </span>
                <span className="text-muted-foreground block truncate text-xs">
                  {skill.description}
                </span>
              </span>
              <span className="text-muted-foreground shrink-0 text-xs">
                {skill.scope === "firm" ? "Firm" : "Personal"}
              </span>
            </button>
          ))
        )}
      </div>
      {mentions.hasMore && !mentions.pending && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-1 w-full"
          disabled={mentions.loadingMore}
          onClick={mentions.loadMore}
        >
          {mentions.loadingMore ? "Loading…" : "More skills"}
        </Button>
      )}
      <div className="text-muted-foreground border-border/60 mt-2 border-t px-3 pt-2 pb-1 text-[11px]">
        ↑ ↓ to browse · Enter to choose · Esc to close
      </div>
    </div>
  );
}
